// The Waivers log past the view's newest hundred (B-526), read on asking: a
// real league's from `GET …/transactions?filter=all&cursor=` with the view's
// `logCursor`, the practice league's from the simulator's whole log.
//
// WHAT WAS READ FOLLOWS THE VIEW. A view read again — after a move, a
// commissioner's reversal, a return to the tab — re-reads as many older
// entries as were on screen, from the new view's cursor: a move made since
// pushes its hundred down by one, and an entry already read can have been
// reversed. Entries are merged by id, the view's copy first, so none shows
// twice while the re-read is on its way. The practice league's log is in this
// browser, so it is sliced afresh each time: how many were asked for, not
// which.
//
// A PAGE THAT FAILS SAYS SO (`failure`) and keeps what is shown, as the
// commissioner log's Older does (`audit.ts`).

import { useEffect, useMemo, useState } from 'react';
import { LogPage, WAIVERS_LOG_LIMIT, type LogEntry, type WaiversView } from '@fsp/domain';
import { waiversLogPage } from '@fsp/sim';
import { get, messageOf, type Reply } from './api';
import { useLeagueSource } from './leagueSource';
import { useSim } from './SimProvider';

const idOf = (e: LogEntry) => (e.kind === 'run' ? `run:${e.run.id}` : `tx:${e.transaction.id}`);

/** The most entries one read asks for (`LogQuery.limit`'s ceiling). */
const MOST_A_READ = 200;

interface Read {
  /** The league (and source) these entries are of; another league's are not shown. */
  key: string;
  /** The view they follow on from (`updatedAt`): a newer view re-reads them. */
  after: string;
  /** Every entry read past the view's own, oldest last. */
  kept: LogEntry[];
  /** The next page's cursor; null once the log is read to its end. */
  cursor: string | null;
  failure: string | null;
}

/** `count` entries of the log from `cursor`, a read at a time, and where they stop. */
async function readOn(
  leagueId: string,
  cursor: string,
  count: number,
): Promise<{ items: LogEntry[]; cursor: string | null } | { failure: string }> {
  const items: LogEntry[] = [];
  let at: string | null = cursor;
  while (at !== null && items.length < count) {
    const limit = Math.min(MOST_A_READ, count - items.length);
    const reply: Reply<LogPage> = await get(
      `/leagues/${leagueId}/transactions?filter=all&limit=${limit}&cursor=${encodeURIComponent(at)}`,
      LogPage,
    );
    if (reply.status !== 'ok')
      return { failure: messageOf(reply, 'The older entries could not be read. Try again.') };
    items.push(...reply.data.items);
    at = reply.data.nextCursor;
  }
  return { items, cursor: at };
}

/** Entries in the log's order with each id once, the first copy kept. */
function once(entries: readonly LogEntry[]): LogEntry[] {
  const seen = new Set<string>();
  return entries.filter((e) => {
    const id = idOf(e);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function useWaiversLog(
  view: WaiversView,
  leagueId: string | null,
): {
  /** The view's entries and every older one read, newest first. */
  entries: LogEntry[];
  /** True while entries older than `entries` are still to read. */
  more: boolean;
  /** Read the next page; null when there is none, or one is being read. */
  older: (() => void) | null;
  loadingOlder: boolean;
  failure: string | null;
} {
  const source = useLeagueSource();
  const { state, version, now } = useSim();
  const key = `${source}:${leagueId ?? ''}`;
  const [read, setRead] = useState<Read | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const mine = read !== null && read.key === key ? read : null;

  // THE PRACTICE LEAGUE: its whole log is here, so how many were asked for is
  // all that is kept, and they are sliced afresh from the log as it stands.
  const [simCount, setSimCount] = useState<{ key: string; count: number } | null>(null);
  const simExtra = simCount !== null && simCount.key === key ? simCount.count : 0;
  const minute = Math.floor(now / 60000);
  const simPage = useMemo(
    () =>
      source === 'sim' && view.logCursor !== null && simExtra > 0
        ? waiversLogPage(state, minute * 60000, view.logCursor, simExtra)
        : null,
    [source, state, version, minute, view.logCursor, simExtra],
  );

  // A NEWER VIEW RE-READS what was read past it, from its own cursor.
  useEffect(() => {
    if (source !== 'api' || leagueId === null || mine === null) return;
    if (mine.after === view.updatedAt || loadingOlder) return;
    const count = mine.kept.length;
    if (view.logCursor === null || count === 0) {
      setRead({ ...mine, after: view.updatedAt, kept: [], cursor: view.logCursor });
      return;
    }
    // NOT CANCELED when this effect runs again: it runs again the moment
    // `loadingOlder` turns on, and the answer is the league's either way.
    setLoadingOlder(true);
    void readOn(leagueId, view.logCursor, count).then((got) => {
      setLoadingOlder(false);
      // A re-read that fails keeps what is shown, and tries again with the next view.
      setRead(
        'failure' in got
          ? { ...mine, after: view.updatedAt }
          : { ...mine, after: view.updatedAt, kept: got.items, cursor: got.cursor, failure: null },
      );
    });
  }, [source, leagueId, mine, view.updatedAt, view.logCursor, loadingOlder]);

  const entries = useMemo(() => {
    if (simPage !== null) return once([...view.log, ...simPage.items]);
    if (mine === null) return view.log;
    return once([...view.log, ...mine.kept]);
  }, [view.log, simPage, mine]);

  const cursor =
    source === 'sim'
      ? simPage === null
        ? view.logCursor
        : simPage.nextCursor
      : mine === null
        ? view.logCursor
        : mine.cursor;

  const older =
    cursor === null || loadingOlder || leagueId === null
      ? null
      : source === 'sim'
        ? () => setSimCount({ key, count: simExtra + WAIVERS_LOG_LIMIT })
        : () => {
            const before = mine?.kept ?? [];
            setLoadingOlder(true);
            void readOn(leagueId, cursor, WAIVERS_LOG_LIMIT).then((got) => {
              setLoadingOlder(false);
              setRead(
                'failure' in got
                  ? {
                      key,
                      after: mine?.after ?? view.updatedAt,
                      kept: before,
                      cursor,
                      failure: got.failure,
                    }
                  : {
                      key,
                      after: mine?.after ?? view.updatedAt,
                      kept: once([...before, ...got.items]),
                      cursor: got.cursor,
                      failure: null,
                    },
              );
            });
          };

  return {
    entries,
    more: cursor !== null,
    older,
    loadingOlder,
    failure: mine?.failure ?? null,
  };
}
